import Image, { ImageProps } from 'next/image';

import { LogoName } from '@sola/schema/lib/logos.js';

import { cn } from '../../utils/helpers.js';

const logoColors = {
  blue: 'blue',
  black: 'black',
  white: 'white',
  full: 'full',
  blueLegacy: 'blueLegacy',
  gray: 'gray',
} as const;
type LogoColors = (typeof logoColors)[keyof typeof logoColors];

export type LogoChooserProps = Omit<ImageProps, 'src' | 'alt' | 'color'> & {
  name: LogoName;
  alt?: string;
  color?: LogoColors;
  priority?: boolean;
};

type LogoImgData = {
  base: string;
  defaultColor?: string;
  width: number;
  height: number;
  colors?: LogoColors[];
};

const logoSVG: Record<LogoName, LogoImgData> = {
  compassion: {
    base: 'LogoCompassion',
    defaultColor: logoColors.blue,
    width: 126,
    height: 48,
    colors: [
      logoColors.black,
      logoColors.blue,
      logoColors.white,
      logoColors.gray,
      logoColors.blueLegacy,
    ],
  },
  'compassion-explorer': {
    base: 'LogoCompassionExplorer',
    defaultColor: logoColors.full,
    width: 75,
    height: 24,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.full,
    ],
  },
  'compassion-journey': {
    base: 'LogoCompassionJourney',
    width: 76,
    height: 18,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-moments': {
    base: 'LogoCompassionMoments',
    width: 75,
    height: 19,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-simple': {
    base: 'LogoCompassionSimple',
    defaultColor: logoColors.blue,
    width: 148,
    height: 59,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.blue,
      logoColors.blueLegacy,
    ],
  },
  'compassion-sunday': {
    base: 'LogoCompassionSunday',
    width: 76,
    height: 22,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-experience': {
    base: 'LogoCompassionExperience',
    defaultColor: logoColors.full,
    width: 145,
    height: 56,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.full,
    ],
  },
  skippy: {
    base: 'LogoSkippy',
    width: 48,
    height: 48,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.blue,
      logoColors.blueLegacy,
    ],
  },
  'compassion-accelerators': {
    base: 'LogoCompassionAccelerators',
    width: 30,
    height: 30,
  },
  'compassion-accelerators-text': {
    base: 'LogoCompassionAcceleratorsText',
    width: 130,
    height: 30,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-multipliers': {
    base: 'LogoCompassionMultipliers',
    width: 30,
    height: 30,
  },
  'compassion-multipliers-text': {
    base: 'LogoCompassionMultipliersText',
    width: 117,
    height: 30,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-nurturers': {
    base: 'LogoCompassionNurturers',
    width: 30,
    height: 30,
  },
  'compassion-nurturers-text': {
    base: 'LogoCompassionNurturersText',
    width: 111,
    height: 30,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-responders': {
    base: 'LogoCompassionResponders',
    width: 30,
    height: 30,
  },
  'compassion-responders-text': {
    base: 'LogoCompassionRespondersText',
    width: 122,
    height: 30,
    colors: [logoColors.black, logoColors.white],
  },
  'apple-pay': {
    base: 'LogoApplePay',
    width: 55,
    height: 23,
    colors: [logoColors.black, logoColors.white],
  },
  'trust-charity-watch': {
    base: 'LogoTrustCharityWatch',
    width: 53,
    height: 40,
  },
  'trust-ecfa': { base: 'LogoTrustECFA', width: 40, height: 40 },
  'trust-bbb': { base: 'LogoTrustBBB', width: 27, height: 40 },
  'trust-guide-star': { base: 'LogoTrustGuideStar', width: 41, height: 40 },
  'trust-charity-navigator': {
    base: 'LogoTrustCharityNavigator',
    width: 78,
    height: 25,
  },
  'trust-norton': { base: 'LogoTrustNorton', width: 58, height: 32 },
  'trust-seal-of-transparency-guide-star': {
    base: 'LogoTrustSealOfTransparencyGuideStar',
    width: 40,
    height: 40,
  },
};

const getLogoData = (name: LogoName, color?: LogoColors) => {
  const logoData = logoSVG[name];

  const hasSelectedColor = color && logoData.colors?.includes(color);

  let { base } = logoData;

  if (hasSelectedColor) {
    base = `${base}--${color}`;
  } else if (logoData.defaultColor) {
    base = `${base}--${logoData.defaultColor}`;
  }

  return {
    base,
    width: logoData.width,
    height: logoData.height,
  };
};

export const LogoChooser = ({
  name,
  alt,
  width,
  height,
  color,
  priority = false,
  ...props
}: LogoChooserProps) => {
  const logoData = getLogoData(name, color);

  let logoWidth: number;
  let logoHeight: number;

  // Calculate correct dimensions based on what's provided
  if (width && !height) {
    logoWidth = width as number;
    logoHeight = (logoData.height / logoData.width) * logoWidth;
  } else if (height && !width) {
    logoHeight = height as number;
    logoWidth = (logoData.width / logoData.height) * logoHeight;
  } else {
    // Both specified: use both
    logoWidth = (width as number) ?? logoData.width;
    logoHeight = (height as number) ?? logoData.height;
  }

  return (
    <Image
      {...props}
      className={cn('LogoChooser LogoChooser__svg', props.className)}
      src={`/logos/${logoData.base}.svg`}
      alt={alt ?? name}
      width={logoWidth}
      height={logoHeight}
      priority={priority}
    />
  );
};
